/**
 * PK-QA-1 — the unified "soft confirm" contract (decision-log.md 2026-07-20
 * "PK-QA-1 — единый confirm-required паттерн").
 *
 * Several user actions are NOT hard-invalid but still warrant an explicit
 * "are you sure?" before they proceed (run a paid LLM with no material, advance
 * a stage that was never analyzed, complete a meeting with no notes). Instead of
 * a bespoke `{ warning }` flag per endpoint, the project standardises ONE
 * mechanism, promoted from the pre-existing `meeting-complete` idiom:
 *
 *   - the server answers such an action with HTTP **412 Precondition Failed**
 *     and the {@link confirmRequiredErrorSchema} body,
 *   - the body carries a machine `reason` sub-code so the client picks the right
 *     confirm text, and a human-readable `message` fallback,
 *   - the client re-issues the SAME request with `confirmed: true` (a single
 *     optional boolean flag reused across every soft-gated endpoint) to override.
 *
 * Hard validation (missing client/role/contact) is deliberately NOT this — that
 * stays a non-overridable 400 `VALIDATION_ERROR`.
 *
 * Shared by BOTH `@eer/api` (the three route gates: pre-kickoff analysis, stage
 * advance, meeting-complete) and `@eer/web` (one `isConfirmRequired` guard that
 * branches on `reason`).
 */
import { z } from 'zod';
/**
 * The machine sub-codes carried by a CONFIRM_REQUIRED 412 body. Each maps to a
 * specific soft gate the front-end renders its own copy for:
 *   - `nothing_to_analyze` — pre-kickoff AI run with no analyzable material,
 *   - `no_analysis_yet`    — Pre-Kickoff(1)→Kick-off(2) with no analysis artifact,
 *   - `empty_meeting`      — meeting-complete with empty notes AND transcript.
 */
export const CONFIRM_REQUIRED_REASONS = [
    'nothing_to_analyze',
    'no_analysis_yet',
    'empty_meeting',
];
/**
 * The 412 body every soft gate returns. `code` is the fixed literal
 * `CONFIRM_REQUIRED`; `reason` is the machine sub-code the front-end guard reads
 * to choose its confirm dialog; `message` is a human-readable RU fallback.
 */
export const confirmRequiredErrorSchema = z.object({
    error: z.object({
        code: z.literal('CONFIRM_REQUIRED'),
        reason: z.enum(CONFIRM_REQUIRED_REASONS),
        message: z.string(),
    }),
});
/**
 * Default human-readable message per reason (server fallback — the front-end
 * ultimately renders its own copy keyed off `reason`, per PK-QA-1 point 4). The
 * `empty_meeting` text intentionally keeps the pre-existing meeting-complete
 * string so the retrofit is additive and never breaks that gate.
 */
export const CONFIRM_REQUIRED_MESSAGES = {
    nothing_to_analyze: 'There is no material to analyse yet. Run the analysis anyway?',
    no_analysis_yet: 'The analysis has never been run. Move to the next stage anyway?',
    empty_meeting: 'Empty meeting requires confirmation',
};
/**
 * Build the {@link confirmRequiredErrorSchema} 412 body for a soft gate. A single
 * builder shared by all three route gates keeps the wire shape identical
 * everywhere (one client guard parses them all). `message` defaults to the
 * canonical per-reason RU copy but may be overridden.
 */
export function confirmRequiredBody(reason, message = CONFIRM_REQUIRED_MESSAGES[reason]) {
    return { error: { code: 'CONFIRM_REQUIRED', reason, message } };
}
/**
 * Front-end guard: given an unknown error body, return its {@link
 * ConfirmRequiredReason} when it is a valid CONFIRM_REQUIRED 412 body, else
 * `null`. Both route consumers (Run Analysis, Next) branch on this.
 */
export function isConfirmRequired(body) {
    const parsed = confirmRequiredErrorSchema.safeParse(body);
    return parsed.success ? parsed.data.error.reason : null;
}
//# sourceMappingURL=confirm.js.map